import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { attentionKeys, feedKeys, folderKeys, filesOverviewKeys } from '~/lib/query-keys';
import type { TrashAction } from './use-files';
import { catalogueKeys } from './use-knowledge-catalogue';
import { knowledgeEnrichmentKeys } from './use-knowledge-enrichment';
import { knowledgeReviewKeys } from './use-knowledge-review';
import { knowledgeSearchKeys } from './use-knowledge-search';

export interface DocumentMeta {
  filename: string;
  title: string;
  date: string;
  tags: string[];
  summary: string;
  /** Set when the document is a coding-session journal (FRONT-334) — the resume handle. */
  codingSessionId: string | null;
  createdAt: string;
  updatedAt: string;
  /** When the document was moved to the recycle bin (FRONT-594); null when live. */
  trashedAt?: string | null;
  /**
   * `ts_headline` excerpt from a CONTENT match on a command-palette search hit
   * (FRONT-873). Present (string or null) only on search results; absent on
   * the plain document list. Null when the row matched on title/tags/summary
   * only.
   */
  snippet?: string | null;
}

export interface DocumentFull extends DocumentMeta {
  content: string;
}

export const documentKeys = {
  all: ['documents'] as const,
  list: () => [...documentKeys.all, 'list'] as const,
  trash: () => [...documentKeys.all, 'trash'] as const,
  detail: (filename: string) => [...documentKeys.all, 'detail', filename] as const,
  search: (query: string) => [...documentKeys.all, 'search', query] as const,
};

/**
 * `enabled` (FRONT-1085) defaults to `true` — unchanged for every existing
 * caller. See `useFiles`'s matching option for why.
 */
export function useDocuments(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: documentKeys.list(),
    queryFn: async () => {
      const r = await api.get<{ documents: DocumentMeta[] }>('/api/documents');
      return r.documents ?? [];
    },
    enabled: opts?.enabled ?? true,
  });
}

/**
 * Search memory documents by relevance (FRONT-872 — command palette content
 * search). Capped server-side at 5 hits. Disabled until the query is
 * non-empty so an idle search box makes no request.
 */
export function useDocumentSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: documentKeys.search(q),
    queryFn: async () => {
      const r = await api.get<{ documents: DocumentMeta[] }>(
        `/api/documents?q=${encodeURIComponent(q)}&limit=5`,
      );
      return r.documents ?? [];
    },
    enabled: q.length > 0,
  });
}

/** Move a single document to the recycle bin (soft delete, FRONT-594). A doc
 * lives in both Files and the inbox feed, so invalidate both. */
export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filename: string) =>
      api.delete(`/api/documents/${encodeURIComponent(filename)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      qc.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-1605 review, finding M2 — see use-files.ts's matching comment.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
    onError: (err) => toast.error(`Failed to delete document: ${(err as Error).message}`),
  });
}

/**
 * Bulk recycle-bin action on documents (FRONT-594): trash / restore / purge a
 * set of filenames. Documents appear in both Files and the inbox feed, so both
 * are invalidated.
 */
export function useBulkDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, filenames }: { action: TrashAction; filenames: string[] }) =>
      api.post<{ affected: number }>('/api/documents/bulk', { action, ids: filenames }),
    onSuccess: (_, { action }) => {
      qc.invalidateQueries({ queryKey: documentKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      qc.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-776 code-review round 3, finding 10: only a PURGE cascades a
      // folder-member detach server-side (`detachPurgedFolderMembers`,
      // src/memory/documents-store.ts) — same reasoning as `useBulkFiles`'s
      // matching fix in use-files.ts.
      if (action === 'purge') qc.invalidateQueries({ queryKey: folderKeys.all });
      // FRONT-1605 review, finding M2 — see use-files.ts's matching comment.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      // FRONT-2062 F16: trash/restore/purge changes whether a document is
      // eligible for the stale-review list and the faceted knowledge
      // search — same two keys `useApplyKnowledgeReviewAction`'s own
      // apply invalidates. Without this, "Undo" on the toast after
      // deleting an item FROM the review screen (`showUndoToast`'s
      // `onUndo` in `knowledge-review-dialog.tsx`, which calls this
      // mutation directly rather than going through the review apply
      // route) restores the document but leaves the review list showing
      // it as already gone.
      qc.invalidateQueries({ queryKey: knowledgeReviewKeys.lists() });
      // The sidebar's Knowledge badge counts the same queue (/api/attention).
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      qc.invalidateQueries({ queryKey: knowledgeSearchKeys.all });
    },
  });
}

/**
 * Rename an agent document inline (FRONT-590) — changes only the display title
 * (the filename key and its backlinks stay intact). No error toast here: the
 * shared inline-rename control owns the toasts so chats and files behave the
 * same. Invalidates the document list so the unified Files view refreshes.
 */
export function useRenameDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ filename, title }: { filename: string; title: string }) =>
      api.patch<{ filename: string; title: string }>(
        `/api/documents/${encodeURIComponent(filename)}`,
        { title },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-1605 review, finding M2 — a rename changes the title the
      // catalogue overview teases the item under.
      qc.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
  });
}

export function useDocumentDetail(
  filename: string | null | undefined,
  opts: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: documentKeys.detail(filename ?? ''),
    queryFn: () =>
      // FRONT-1453: `title` is a live source for the document's heading,
      // distinct from `filename` — the inbox modal's reader prefers it over
      // its own (possibly stale) chat-title snapshot.
      api.get<{
        filename: string;
        content: string;
        title: string;
        createdAt: string;
        updatedAt: string;
        /** FRONT-2550: the document's current tags (what the Files screen filters on). */
        okfTags?: string[];
        /** FRONT-2550: when Eddy tagged it; null if it did not or every tag was removed. */
        taggedByEddyAt?: string | null;
        /** Set when the document is in the recycle bin (its tags are then read-only). */
        trashedAt?: string | null;
      }>(
        `/api/documents/${encodeURIComponent(filename!)}`,
      ),
    enabled: !!filename && (opts.enabled ?? true),
    // Callers can opt out of the global 60s staleTime — the inbox modal passes
    // 0 so reopening always re-pulls the document (FRONT-221: it may have just
    // been rewritten by a background "Update document" turn).
    ...(opts.staleTime !== undefined ? { staleTime: opts.staleTime } : {}),
  });
}
